import { Component, Input, OnInit } from '@angular/core';
import { UIRouter }          from '@uirouter/core';
import { NgbModal }          from '@ng-bootstrap/ng-bootstrap';
import { TranslateService }  from '@ngx-translate/core';

import { ApiService }          from '../api/api.service';
import { CompanyService }      from '../companies/company.service';
import { ConfirmService }      from '../messages/confirm/confirm.service';
import { CurrencyModel }      from '../core/models/currency';
import { MessagesService }     from '../messages/messages.service';
import { ProductIntegrationsService } from './product-integrations.service';
import { ProductModel }        from './models/product';
import { ProductService }      from './product.service';
import { SelectOption }        from '../core/models/select-option';
import { SessionModel }        from '../sessions/models/session';
import { SessionService }      from '../sessions/session.service';
import { StreamSearchService } from '../api/stream-search.service';

import VendorSearchResultsHtml from '../vendors/templates/vendors.search.results.html';

@Component({
  selector   : 'wor-product',
  template: require('./product.component.html')
})
export class ProductComponent implements OnInit {
  @Input() categories: SelectOption[];
  @Input() invoices: Record<string, unknown>[];
  @Input() log: Record<string, unknown>[];
  @Input() orders: Record<string, unknown>[];
  @Input() product: ProductModel;
  @Input() reserved: Record<string, unknown>[];
  @Input() serialNumbers: Record<string, unknown>[];
  @Input() vendorInvoices: Record<string, unknown>[];

  category: { category_name?: string; id?: string; purchase_tax_rate?: string } = {};
  isLaborNRMA = false;
  loading = false;
  originalItemCode: string;
  product_groups: SelectOption[];
  product_group: any;
  product_types: SelectOption[];
  qtyAvailable: string;
  stockTransfers: Record<string, unknown>[] = [];
  vendorSearch: any;
  wasHeadOfficeChecked = false;

  constructor (
    public  integrationsService: ProductIntegrationsService,
    public  productApi: ProductService,
    private apiService: ApiService,
    public companyService: CompanyService,
    private confirmService: ConfirmService,
    private messagesService: MessagesService,
    private modal: NgbModal,
    private router: UIRouter,
    public  sessionService: SessionService,
    private streamSearchService: StreamSearchService,
    private translateService: TranslateService
  ) {}

  get session () : SessionModel { return this.sessionService.base(); }
  get currency () : CurrencyModel { return this.sessionService.currency(); }

  checkRights (module: string): boolean {


    return this.sessionService.hasRights(module);


  }

  ngOnInit () {
    if (!this.product) {
      this.product = {
        id: null, item_code: null, description: null, description2: null, brand: null,
        product_type: 'S', vendor_id: null, vendor_name: null, quantity_on_hand: null,
        dont_update_quantity: false, comment: null, job_card_comment: null, location: null,
        retail_price: null, price2: null, price3: null, price4: null, cost: null,
        cost_including_tax: null, gst_free: null, service: null, allow_partial_qty: null
      };
    }

    this.originalItemCode = this.product.item_code;
    this.qtyAvailable = this.productApi.calculateQtyAvailable(this.product);

    this.vendorSearch = {
      list: [],
      selected: null as any,
      templateUrl: VendorSearchResultsHtml,
      choose: (vendor) => {
        this.product.vendor_id = vendor.id;
        this.product.vendor_name = vendor.company_name;
      },
      format: (vendor) => vendor ? vendor.company_name : '',
      search: (typed) => this.streamSearchService.search('vendors', typed)
        .then(vendors => this.vendorSearch.list.concat(vendors))
    };

    this.isLaborNRMA = this.companyService.isBrand?.('nrma')
      && this.companyService.isNRMAUser?.('create_job_codes')
      && this.productApi.isLabor(this.product);

    this.integrationsService.init({ product: this.product });
    this.init();
  }

  private init () {
    if (this.product.vendor_id) this.getVendor(this.product.vendor_id);
    if (this.product.product_group_name) this.getProductGroup();
    if (this.companyService.isMultiBranch()) {
      this.productApi.getStockTransfers(this.product).then(t => this.stockTransfers = t);
    }
    this.getCategory();
    this.initProductTypes();
    this.initProductGroups();
  }

  addGroup () {
    this.productApi.quickAddGroup(this.categories)
    .then(group => {
      this.refreshGroups().then(groups => {
        this.product_groups = groups;
        this.product_group = group;
        this.product.product_group_name = group.name;
      });
    });
  }

  checkHeadOffice () {
    if (this.isItemCodeUntouched() || this.wasHeadOfficeChecked) return;
    this.productApi.findInHeadOffice(this.product).then(product => {
      if (product) {

        this.product = product;

        if (this.product.vendor_id) this.getVendor(this.product.vendor_id);

      }
      this.wasHeadOfficeChecked = true;
    }).finally(() => this.wasHeadOfficeChecked = true);
  }

  costExcludingTaxUpdated () {


    this.product.cost_including_tax = this.product.gst_free
      ? this.product.cost
      : this.productApi.calculateCostWithTax(this.product.cost, this.category.purchase_tax_rate);


  }

  costIncludingTaxUpdated () {


    this.product.cost = this.product.gst_free
      ? this.product.cost_including_tax
      : this.productApi.calculateCostWithoutTax(this.product.cost_including_tax, this.category.purchase_tax_rate);


  }

  delete () {
    this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.DELETE_PRODUCT'))
    .then(() => this.apiService.delete('/product', this.product.id))
    .then(() => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.DELETED.PRODUCT'), 'success');
      this.router.stateService.go('app.products');
    }).catch(err => this.messagesService.error(err));
  }

  getCategory (id?: string) {
    if (id) {
      this.apiService.get('/product_category', id).then(category => {
        this.product.product_category_name = category.name;
        this.category = { category_name: category.name, id: category.id, purchase_tax_rate: category.purchase_tax_rate || this.currency.purchases_tax_rate };
        if (this.product.cost) this.costExcludingTaxUpdated();
      }).catch(err => this.messagesService.error(err));
    } else {
      this.product.product_category_name = '';
      this.category = { category_name: '', id: '', purchase_tax_rate: this.currency.purchases_tax_rate };
      if (this.product.cost) this.costExcludingTaxUpdated();
    }
  }

  getProductGroup () {
    if (this.product.product_group_name) {
      this.apiService.get('/product_group', this.product.product_group_name, undefined, { skipEncoding: true })
      .then(group => {
        this.product_group = group;
        if (this.product_group?.length) this.getCategory(this.product_group[0].product_category_id);
      });
    } else {
      this.getCategory();
    }
  }

  getVendor (id: string) {
    this.apiService.get('/vendor', id).then(vendor => {
      this.product.vendor_name = vendor.company_name;
      this.vendorSearch.selected = { company_name: vendor.company_name, id: vendor.id };
    }).catch(err => this.messagesService.error(err));
  }

  initProductGroups () {


    this.apiService.get('/product_groups').then(groups => this.product_groups = groups);


  }
  initProductTypes () {

    this.apiService.get('/system/product_types/', null, 'productTypes').then(types => this.product_types = types);

  }

  isItemCodeUntouched () {


    return this.originalItemCode === this.product.item_code;


  }

  onItemCodeChange () {


    this.wasHeadOfficeChecked = false;


    this.checkHeadOffice();


  }

  pushDownPricingInfo () {
    Promise.resolve()
    .then(() => this.productApi.pushDownPricingInfo(this.product))
    .then(() => {
      this.messagesService.show('Pricing information pushed down', 'success');
      this.router.stateService.reload();
    }).catch(err => this.messagesService.error(err));
  }

  refreshGroups () {


    return this.apiService.get('/product_groups');


  }

  save () {
    this.productApi.save(this.product).then(product => {
      this.product = product;
      return this.product.id
        ? this.apiService.get('/products/product_bom_item_count', this.product.id)
        : Promise.resolve(0);
    }).then(count => count
      ? this.confirmService.generic(this.translateService.instant('JS_SPACE.MESSAGES.PRODUCT_ATTACHED_TO_BUNDLE'))
        .then(() => this.apiService.patch('/bom_items/update_retail_price', this.product, 'product'))
        .catch(() => Promise.resolve())
      : Promise.resolve()
    ).then(() => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.PRODUCT'), 'success');
      this.router.stateService.go('app.product', { id: this.product.id }, { reload: true });
    }).catch(err => this.messagesService.error(err));
  }

  syncWithHeadOffice () {
    Promise.resolve()
    .then(() => this.productApi.save(this.product))
    .then(() => this.apiService.patch('/products/sync_multibranch_product_entry', this.product))
    .then(() => {
      this.messagesService.show(this.translateService.instant('MULTI_BRANCH_SPACE.PRODUCT_LINKED'), 'success');
      this.router.stateService.reload();
    }).catch(err => this.messagesService.error(err));
  }

  toggleGSTFree () {


    this.product.cost_including_tax = this.product.gst_free
      ? this.product.cost
      : this.productApi.calculateCostWithTax(this.product.cost, this.product);


  }

  validateItemCode (itemCode: string) {


    return this.productApi.validateItemCode({ item_code: itemCode });


  }

}
